import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { firebase } from './firebaseConfig';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

async function getIdToken(force = false) {
  const user = getAuth(firebase).currentUser;
  return user ? user.getIdToken(force) : null;
}

async function authHeaders(force = false) {
  const t = await getIdToken(force);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function toFriendlyError(response) {
  let message;
  let code;
  let details;
  let payload;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (payload && typeof payload === 'object') {
    code = payload.code;
    details = payload.details;
    if (typeof payload.message === 'string' && payload.message.trim()) {
      message = payload.message;
    } else if (typeof payload.title === 'string' && payload.title.trim()) {
      message = payload.title;
    }

    if (code === 'RATE_LIMITED' && payload?.details?.retryAfterSeconds) {
      const retrySeconds = Number(payload.details.retryAfterSeconds);
      if (Number.isFinite(retrySeconds) && retrySeconds > 0) {
        const rounded = Math.round(retrySeconds);
        message = `Too many requests - try again in ~${rounded}s.`;
      }
    }
  }

  if (!message) {
    const statusText = response.statusText ? ` ${response.statusText}` : '';
    message = `Something went wrong (HTTP ${response.status}${statusText})`;
  }

  const error = new Error(message);
  error.status = response.status;
  error.statusText = response.statusText;
  if (code) error.code = code;
  if (details !== undefined) error.details = details;
  if (payload !== undefined) error.body = payload;
  return error;
}

// Utility: wait for Firebase auth state to be ready before first fetch
export function waitForAuthReady() {
  const auth = getAuth(firebase);
  return new Promise(resolve => {
    const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); });
  });
}

export async function fetchWithAuth(path, options = {}) {
  const url = backendUrl + path;
  const baseHeaders = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const initialAuth = await authHeaders();
  let response = await fetch(url, { ...options, headers: { ...baseHeaders, ...initialAuth } });
  if (response.status === 401) {
    try {
      // one retry with a forced token refresh
      const refreshed = await authHeaders(true);
      response = await fetch(url, { ...options, headers: { ...baseHeaders, ...refreshed } });
    } catch {
      // ignore and fall through to error handling below
    }
  }
  if (!response.ok) {
    throw await toFriendlyError(response);
  }
  return response;
}

export async function fetchWithoutAuth(path, options = {}) {
  const url = backendUrl + path;
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    throw await toFriendlyError(response);
  }
  return response;
}
